import { getApolloClient } from '@faustwp/core';
import { CaseStudyPage } from '../../components/CaseStudyPage';
import {
  GET_POST_BY_SLUG_AND_CATEGORY,
  GET_POST_BY_SLUG_AND_CATEGORY_MINIMAL,
  GET_POST_BY_SLUG_AND_LANGUAGE_FI,
  GET_POST_BY_SLUG_TRANSLATIONS_MINIMAL,
  GET_POST_BY_DATABASE_ID,
  GET_POST_BY_DATABASE_ID_MINIMAL,
  GET_POST_TRANSLATIONS_BY_DATABASE_ID,
} from '../../lib/queries/caseStudyBySlug';
import { FINNISH_CATEGORY_SLUG } from '../../lib/caseStudyPaths';

function getEnglishTranslation(post) {
  const translations = post?.translations ?? [];
  const code = (c) => (c ?? '').toLowerCase();
  return translations.find((t) => code(t?.language?.code).startsWith('en')) ?? null;
}

/** Pick the Finnish translation (Polylang). language.code can be 'fi', 'fi_FI', etc. */
function getFinnishTranslation(post) {
  const translations = post?.translations ?? [];
  const code = (c) => (c ?? '').toLowerCase();
  return translations.find((t) => code(t?.language?.code).startsWith('fi')) ?? null;
}

/** True when the post's language is Finnish. */
function isFinnishPost(node) {
  const code = (node?.language?.code ?? '').toLowerCase();
  return code.startsWith('fi');
}

export default function TyommeCaseStudyPage({ post, generalSettings, enSlug, canonicalPath }) {
  if (!post) return null;
  return (
    <CaseStudyPage
      post={post}
      generalSettings={generalSettings}
      lang="fi"
      enSlug={enSlug}
      canonicalPath={canonicalPath}
    />
  );
}

export async function getStaticProps(ctx) {
  const { slug } = ctx.params ?? {};
  if (!slug) {
    return { notFound: true };
  }
  const client = getApolloClient();
  let post = null;
  let generalSettings = null;
  let data = null;

  // 1) Try Polylang language filter: fetch Finnish post by slug + language FI
  if (!post) {
    try {
      const res = await client.query({
        query: GET_POST_BY_SLUG_AND_LANGUAGE_FI,
        variables: { slug },
      });
      data = res?.data;
      post = data?.posts?.nodes?.[0] ?? null;
      generalSettings = data?.generalSettings ?? null;
    } catch {
      // Schema may not have language filter or uses different enum (e.g. FI)
    }
  }

  // 2) Polylang: fetch by slug (no language filter). Pick Finnish node from results, or FI translation's databaseId, then fetch full post.
  if (!post) {
    try {
      const res = await client.query({
        query: GET_POST_BY_SLUG_TRANSLATIONS_MINIMAL,
        variables: { slug },
      });
      data = res?.data;
      const nodes = data?.posts?.nodes ?? [];
      const fiNode = nodes.find((n) => isFinnishPost(n));
      const anyNode = nodes[0] ?? null;
      const fiTranslation = anyNode ? getFinnishTranslation(anyNode) : null;
      const idToFetch = fiNode?.databaseId ?? fiTranslation?.databaseId;
      if (idToFetch) {
        try {
          const fullRes = await client.query({
            query: GET_POST_BY_DATABASE_ID,
            variables: { databaseId: String(idToFetch) },
          });
          const fullData = fullRes?.data;
          if (fullData?.post) {
            post = fullData.post;
            data = fullData;
            generalSettings = fullData?.generalSettings ?? null;
          }
        } catch {
          const minimalRes = await client.query({
            query: GET_POST_BY_DATABASE_ID_MINIMAL,
            variables: { databaseId: String(idToFetch) },
          });
          const minimalData = minimalRes?.data;
          if (minimalData?.post) {
            post = minimalData.post;
            data = minimalData;
            generalSettings = minimalData?.generalSettings ?? null;
          }
        }
      }
      if (!post && fiNode) {
        post = fiNode;
        generalSettings = data?.generalSettings ?? null;
      }
    } catch {
      // Schema may not have language/translations
    }
  }

  // 3) Fallback: fetch by slug + Finnish category (tyomme)
  if (!post) {
    try {
      const res = await client.query({
        query: GET_POST_BY_SLUG_AND_CATEGORY,
        variables: { slug, categorySlug: FINNISH_CATEGORY_SLUG },
      });
      data = res?.data;
      const nodes = data?.posts?.nodes ?? [];
      post = nodes[0] ?? null;
      generalSettings = data?.generalSettings ?? null;
      // If multiple nodes (FI + EN in same category), prefer Finnish
      if (nodes.length > 1 && nodes.some((n) => n.language && isFinnishPost(n))) {
        post = nodes.find((n) => isFinnishPost(n)) ?? post;
      }
    } catch {
      try {
        const res = await client.query({
          query: GET_POST_BY_SLUG_AND_CATEGORY_MINIMAL,
          variables: { slug, categorySlug: FINNISH_CATEGORY_SLUG },
        });
        data = res?.data;
        post = data?.posts?.nodes?.[0] ?? null;
        generalSettings = data?.generalSettings ?? null;
      } catch {
        // Keep post null
      }
    }
  }

  if (!post) {
    return { notFound: true };
  }

  // Resolve Finnish post if current is EN: fetch translations, then full FI post by databaseId. Also get enSlug for EN link.
  let enSlug = null;
  try {
    let trNode = null;
    try {
      const caseTrRes = await client.query({
        query: GET_CASE_STUDY_BY_DATABASE_ID,
        variables: { databaseId: String(post.databaseId) },
      });
      trNode = caseTrRes?.data?.caseStudy ?? null;
    } catch {
      // CPT single query not in schema
    }
    if (!trNode) {
      const trRes = await client.query({
        query: GET_POST_TRANSLATIONS_BY_DATABASE_ID,
        variables: { databaseId: String(post.databaseId) },
      });
      trNode = trRes?.data?.post ?? null;
    }
    const enTranslation = trNode ? getEnglishTranslation(trNode) : null;
    enSlug = enTranslation?.slug ?? null;
    const nodeLang = (trNode?.language?.code ?? '').toLowerCase();
    const currentIsFinnish = nodeLang.startsWith('fi');
    const fiTranslation = trNode ? getFinnishTranslation(trNode) : null;
    const fiDatabaseId = currentIsFinnish ? trNode?.databaseId : fiTranslation?.databaseId;
    if (!currentIsFinnish && fiDatabaseId && fiDatabaseId !== post.databaseId) {
      try {
        const caseRes = await client.query({
          query: GET_CASE_STUDY_BY_DATABASE_ID,
          variables: { databaseId: String(fiDatabaseId) },
        });
        if (caseRes?.data?.caseStudy) {
          post = caseRes.data.caseStudy;
        }
      } catch {
        try {
          const fullRes = await client.query({
            query: GET_POST_BY_DATABASE_ID,
            variables: { databaseId: String(fiDatabaseId) },
          });
          const fullData = fullRes?.data;
          if (fullData?.post) {
            post = fullData.post;
          }
        } catch {
          try {
            const minRes = await client.query({
              query: GET_POST_BY_DATABASE_ID_MINIMAL,
              variables: { databaseId: String(fiDatabaseId) },
            });
            if (minRes?.data?.post) post = minRes.data.post;
          } catch {
            // Keep current post
          }
        }
      }
    }
  } catch {
    // WPGraphQL Polylang not active or no translations
  }

  return {
    props: { post, generalSettings, enSlug: enSlug ?? null, canonicalPath: `/tyomme/${slug}` },
    revalidate: 60,
  };
}

export async function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}
